import { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import useMediaQuery from '@mui/material/useMediaQuery';
import ActionIconButton from '@components/ActionIconButton';
import FiltersModal from '@components/FiltersModal';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { useTranslation } from '@hooks';
import { clearedInboxFilters, isSameAssignees } from '@helpers/inbox';
import { FiltersIcon } from 'assets/images/icons';

// Scope filters (team, channel, view) moved to the NavigationRail; status toggles live
// in the list header. This panel keeps the secondary filters, including per-assignee
// filtering, which the rail's Mine/Unassigned/All views do not cover.
const FiltersPanel = ({ onApplyFilters, filter, setFilter, assigneesOptions, defaultAssignees }) => {
    const [isFilterOpened, setFilterOpened] = useState(false);
    const [refetchFilters, setRefetchFilters] = useState(false);

    const isMobile = useMediaQuery('@media (max-width: 1024px)');
    const { t } = useTranslation();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            onApplyFilters();
        }
    }, [refetchFilters]);

    const update = {
        timeframe: data => {
            setFilter(prev => ({
                ...prev,
                timeframe: { start: data[0], end: data[1] },
                from: data[0] ? format(new Date(data[0]), 'yyyy-MM-dd') : null,
                to: data[1] ? format(new Date(data[1]), 'yyyy-MM-dd') : null,
            }));
        },
        assignees: value => {
            setFilter(prev => ({ ...prev, assignees: value }));
        },
        showUnreadOnly: e => setFilter(prev => ({ ...prev, showUnreadOnly: e.target.checked })),
    };

    const formOptions = [
        {
            type: 'multiSelect',
            label: t('common.assignedTo'),
            value: filter.assignees,
            onChange: update.assignees,
            options: assigneesOptions,
            countable: true,
            groupBased: true,
            error: !filter.assignees.length,
        },
        {
            type: 'timeframe',
            label: t('filters.timeframe'),
            value: [filter.timeframe.start, filter.timeframe.end],
            onChange: update.timeframe,
        },
        {
            type: 'switch',
            label: t(`filters.showUnreadOnly`),
            value: filter.showUnreadOnly,
            onChange: update.showUnreadOnly,
            style: { gridColumn: isMobile ? '1 / span 1' : '1 / span 2' },
        },
    ];

    const isValid = filter.assignees.length > 0;

    const resetFilters = () =>
        setFilter(prev => ({
            ...prev,
            assignees: defaultAssignees,
            ...clearedInboxFilters(),
        }));
    const hasDraftFilters = useMemo(
        () => !!filter.timeframe.start || filter.showUnreadOnly || !isSameAssignees(filter.assignees, defaultAssignees),
        [filter, defaultAssignees],
    );

    return (
        <>
            <ActionIconButton
                title={t('buttons.filters')}
                onClick={() => setFilterOpened(!isFilterOpened)}
                size="small"
                sx={{
                    '& svg': { width: 16, height: 16 },
                    '& svg path': { fill: 'var(--text-secondary)' },
                }}
            >
                <FiltersIcon />
            </ActionIconButton>
            <FiltersModal
                isOpen={isFilterOpened}
                handleClose={() => setFilterOpened(false)}
                onApply={() => {
                    setRefetchFilters(true);
                    setFilterOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={!hasDraftFilters}
            >
                <FiltersFormCreator type="inbox" options={formOptions} />
            </FiltersModal>
        </>
    );
};

export default FiltersPanel;
